import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Sun } from 'lucide-react';
import { SEASON_CALENDAR, MONTH_LABELS } from '@/lib/constants';

const VARIETY_COLOR = {
  'Haden':              'bg-[hsl(var(--c-mango-orange))]',
  'Carrie':             'bg-[hsl(var(--c-leaf))]',
  'Tommy Atkins':       'bg-[hsl(var(--c-mango-red))]',
  'Kent':               'bg-[hsl(var(--c-mango-yellow))]',
  'Valencia Pride':     'bg-[hsl(var(--c-mango-orange))]',
  'Keitt':              'bg-[hsl(var(--c-leaf))]',
  "Grower's Choice Mix":'bg-[hsl(var(--c-teal))]',
};

export default function SeasonalCalendar({ tone = 'ink' }) {
  const [hover, setHover] = useState(null); // { variety, monthIndex }
  const isCream = tone === 'cream';
  const cellBase = isCream ? 'bg-white/10' : 'bg-[hsl(var(--c-cream-deep))]';
  const monthLabelColor = isCream ? 'text-white/70' : 'text-[hsl(var(--c-ink-soft))]';
  const headerColor = isCream ? 'text-white' : 'text-[hsl(var(--c-ink))]';
  const borderColor = isCream ? 'border-white/30' : 'border-[hsl(var(--c-ink))]';
  const currentMonth = new Date().getMonth();

  return (
    <div className="seasonal-scroll" data-testid="seasonal-calendar-wrap">
      <div className="seasonal-inner">
        <div data-testid="seasonal-calendar" className={`border-2 ${borderColor} ${isCream ? 'bg-white/5' : 'bg-white'}`}>
          {/* Month header row */}
          <div className={`grid grid-cols-[120px_repeat(12,minmax(48px,1fr))] sm:grid-cols-[160px_repeat(12,minmax(0,1fr))] border-b-2 ${borderColor}`}>
            <div className={`px-3 sm:px-4 py-3 text-[10px] uppercase tracking-[0.18em] font-bold ${headerColor} border-r-2 ${borderColor}`}>Variety</div>
            {MONTH_LABELS.map((m, i) => (
              <div
                key={m}
                className={`relative px-1 py-3 text-center text-[10px] uppercase tracking-wider font-bold ${i === currentMonth ? 'bg-[hsl(var(--c-mango-yellow))] text-[hsl(var(--c-ink))]' : monthLabelColor} ${i < 11 ? `border-r ${borderColor}` : ''}`}
              >
                {m}
                {i === currentMonth && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 inline-flex items-center justify-center h-4 w-4 bg-[hsl(var(--c-ink))] text-white"><Sun className="h-2.5 w-2.5" /></span>
                )}
              </div>
            ))}
          </div>

          {/* Rows */}
          {SEASON_CALENDAR.map((row, rIdx) => (
            <div
              key={row.variety}
              className={`grid grid-cols-[120px_repeat(12,minmax(48px,1fr))] sm:grid-cols-[160px_repeat(12,minmax(0,1fr))] ${rIdx < SEASON_CALENDAR.length - 1 ? `border-b ${borderColor}` : ''}`}
            >
              <div className={`px-3 sm:px-4 py-3 font-heading text-xs sm:text-sm md:text-base font-bold ${headerColor} border-r-2 ${borderColor} flex items-center gap-2`}>
                <span className={`h-3 w-3 ${VARIETY_COLOR[row.variety] || 'bg-[hsl(var(--c-mango-orange))]'} border border-[hsl(var(--c-ink))]`} />
                <span className="truncate">{row.variety}</span>
              </div>
              {MONTH_LABELS.map((_, mIdx) => {
                const inSeason = row.months.includes(mIdx);
                const active = hover && hover.variety === row.variety && hover.monthIndex === mIdx;
                return (
                  <button
                    key={mIdx}
                    type="button"
                    onMouseEnter={() => inSeason && setHover({ variety: row.variety, monthIndex: mIdx })}
                    onMouseLeave={() => setHover(null)}
                    onFocus={() => inSeason && setHover({ variety: row.variety, monthIndex: mIdx })}
                    onBlur={() => setHover(null)}
                    aria-label={`${row.variety} in ${MONTH_LABELS[mIdx]}: ${inSeason ? 'in season' : 'off season'}`}
                    className={`relative h-10 sm:h-12 ${mIdx < 11 ? `border-r ${borderColor}` : ''} ${inSeason ? 'cursor-pointer' : ''} ${inSeason ? '' : cellBase}`}
                  >
                    {inSeason && (
                      <motion.span
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1], delay: rIdx * 0.05 + mIdx * 0.02 }}
                        style={{ transformOrigin: 'left center' }}
                        className={`absolute inset-1 ${VARIETY_COLOR[row.variety] || 'bg-[hsl(var(--c-mango-orange))]'} border-2 border-[hsl(var(--c-ink))] ${active ? 'ring-2 ring-offset-2 ring-[hsl(var(--c-mango-yellow))]' : ''}`}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
